import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { ChevronLeft } from 'lucide-react';
import blogPosts from '../data/blogPosts.json';
import { BlogPost as BlogPostType } from '../types/blog';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const posts = blogPosts as BlogPostType[];
  const post = posts.find((post) => post.slug === slug);
  
  useEffect(() => {
    if (!post) {
      navigate('/blog');
    }
  }, [navigate, post]);
  
  if (!post) {
    return null;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{post.title} | SpreadsheetChallenges Blog</title>
        <meta name="description" content={post.summary} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://spreadsheetchallenges.com/blog/${post.slug}`} />
        <meta property="og:image" content={post.image} />
        <meta property="article:published_time" content={post.publishDate} />
        <meta property="article:author" content={post.author} />
      </Helmet>
      
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="flex items-center text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/blog')}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Blog
          </Button>
        </div>
        
        <div className="aspect-video w-full mb-6 overflow-hidden rounded-lg">
          <img 
            src={post.image} 
            alt={post.title}
            className="object-cover w-full h-full"
          />
        </div>
        
        <h1 className="text-4xl font-bold mb-3">{post.title}</h1>
        
        <div className="flex items-center text-muted-foreground mb-8">
          <span>{format(new Date(post.publishDate), 'MMMM d, yyyy')}</span>
          <span className="mx-2">•</span>
          <span>{post.author}</span>
        </div>
        
        <div className="prose dark:prose-invert max-w-none">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
        
        <div className="border-t border-border mt-12 pt-6">
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/blog')}
            >
              Back to All Posts
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost; 